import { defaultApi } from '@/plugins/api/defaultApi'
import { sleep } from '@/util'
import jwtDecode from 'jwt-decode'
import env from './env'
import $store from '@/store'

const TOKEN_NAME = env.state.token.name
const REFRESH_TOKEN_NAME = env.state.token.refreshTokenName

const getToken = () => {
  if (localStorage.getItem(TOKEN_NAME) === 'undefined') {
    localStorage.clear()
  }

  return {
    accessToken: localStorage.getItem(TOKEN_NAME),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_NAME),
  }
}

let refreshLoading = false
const resolves = [] // 토큰 갱신시 블럭걸린 resolve

let meLoading = false
let signinRefreshLoading = false

const meLoadingCheck = async () => {
  if (meLoading === false) {
    return true
  }
  await sleep(10)
  return meLoadingCheck()
}

const setGaUserId = (gtm, userId) => {
  const dataLayer = gtm.dataLayer()
  dataLayer.push({
    userId,
  })
}

const resetGtmDataLayer = (gtm) => {
  const dataLayer = gtm.dataLayer()
  dataLayer.push(function () {
    this.reset()
  })
}

const st = {
  name: 'auth',
  namespaced: true,
  state: {
    isMeLoad: false,
    info: null,
    refresh: null,
    me: null,
    token: null,
    meLoading: false,
  },
  getters: {
    isApproved(state) {
      if (!state.me) {
        return false
      }
      return state.me.isApproved
    },
    isEmptyStoreSet(state) {
      if (!state.me) {
        return false
      }
      return state.me.storeSet.length === 0
    },
    isEmptyBusinessSet(state) {
      if (!state.me) {
        return false
      }
      return state.me.businessSet.length === 0
    },
    isCreditAvailable(state) {
      if (!state.me) {
        return false
      }
      return state.me.businessSet.length > 0 && state.me.storeSet.length > 0
    },
    isPurchaseAvailable(state) {
      if (!state.me) {
        return false
      }
      return state.me.businessSet.length > 0 && state.me.storeSet.length > 0
    },
    getTokenExpireTime(state) {
      if (state.info == null) {
        return null
      }
      return state.info.exp
    },
    getRefreshTokenExpireTime(state) {
      if (state.info == null) {
        return null
      }
      return state.refresh.exp
    },
    hasAnyRole: (state) => (roles) => {
      if (state.info == null) {
        return false
      }
      if (state.info.authorities == null) {
        return false
      }
      return !!state.info.authorities.filter((obj) => {
        return roles.indexOf(obj) >= 0
      }).length
    },
    isAuthenticated(state) {
      return state.info != null
    },
  },
  mutations: {
    setIsMeLoad(state, isMeLoad) {
      state.isMeLoad = isMeLoad
    },
    setMeLoading(state, meLoading) {
      state.meLoading = meLoading
    },
    signinSuccess(state, token) {
      localStorage[TOKEN_NAME] = token.accessToken
      localStorage[REFRESH_TOKEN_NAME] = token.refreshToken

      const sec = 1000
      state.token = token.accessToken
      state.info = jwtDecode(token.accessToken)
      state.info.exp *= sec
      state.refresh = jwtDecode(token.refreshToken)
      state.refresh.exp *= sec

      // set ga4 user id
      setGaUserId(this._vm.$gtm, state.info.uuid)
    },
    signout(state) {
      delete localStorage[TOKEN_NAME]
      delete localStorage[REFRESH_TOKEN_NAME]

      for (const k in state) {
        state[k] = null
      }

      // unset ga4 user id
      resetGtmDataLayer(this._vm.$gtm)
    },
    setMe(state, me) {
      state.me = me
    },
  },
  actions: {
    isTokenExpire({ getters }) {
      const exp = getters.getTokenExpireTime
      if (!exp) {
        return false
      }
      return exp < Date.now()
    },
    isRefreshTokenExpire({ getters }) {
      const exp = getters.getRefreshTokenExpireTime
      if (!exp) {
        return false
      }
      return exp < Date.now()
    },
    signinSuccess({ commit }, token) {
      commit('signinSuccess', token)
      return token
    },
    async signin({ dispatch }, user) {
      const { data } = await defaultApi.signin(user.id, user.password)
      data.userId = data.user.uuid
      await dispatch('signinSuccess', Object.assign(getToken(), data))
      return data
    },
    async getMe({ state, commit, getters }) {
      if (getters.isAuthenticated === false) {
        return
      } else if (meLoading === true) {
        await meLoadingCheck()
        return state.me
      }
      try {
        meLoading = true
        commit('setMeLoading', meLoading) // loading 추가
        const { data } = await defaultApi.getMe()
        commit('setMe', data)
        commit('setIsMeLoad', true)
        return data
      } finally {
        meLoading = false
        commit('setMeLoading', meLoading) // loading 추가
      }
    },
    async init({ dispatch, getters }) {
      if (!getters) {
        return
      }
      if (getToken().refreshToken != null) {
        await dispatch('signinSuccess', getToken())
        await dispatch('sync')
      }
    },
    async signout({ commit }) {
      commit('signout')
      $store.commit('device/setIsSaveFcs', false)
    },
    async refresh({ dispatch }) {
      if (signinRefreshLoading === false) {
        try {
          signinRefreshLoading = true
          const { data } = await defaultApi.signinRefresh(getToken().refreshToken)
          await dispatch('signinSuccess', data)
          return data
        } catch (e) {
          if (e.status === 401) {
            await dispatch('signout')
          }
          throw e
        } finally {
          signinRefreshLoading = false
        }
      }
    },
    async sync({ dispatch }) {
      return new Promise(async (resolve, reject) => {
        if (resolves.length > 0 || refreshLoading) {
          resolves.push(resolve)
        } else if (refreshLoading === false && (await dispatch('isTokenExpire'))) {
          refreshLoading = true
          resolves.push(resolve)
          try {
            if (await dispatch('isRefreshTokenExpire')) {
              await dispatch('signout')
            } else {
              await dispatch('refresh')
            }
          } catch (e) {
            await dispatch('signout')
            reject(e)
          } finally {
            while (resolves.length > 0) {
              const r = resolves.pop()
              r(getToken())
            }
            refreshLoading = false
          }
        } else {
          resolve(getToken().token)
        }
      })
    },
    async findId({}, { phone }) {
      const {
        data: { email },
      } = await defaultApi.getFindId(phone)
      return email
    },
    async requestPasswordReset({}, { email, phone }) {
      const { data } = await defaultApi.postPasswordReset({ email, phone })
      return data
    },
    async requestPasswordResetConfirm({}, params) {
      const { data } = await defaultApi.postPasswordResetConfirm(params)
      return data
    },
    async requestPasswordResetChange({}, params) {
      const { data } = await defaultApi.postPasswordResetChange(params)
      return data
    },
    async reqIncorrectInformation({ state }) {
      await defaultApi.incorrectInformation(state.me.uuid)
    },
  },
}

export default st
